import { useState, useEffect, useRef } from "react";
import { BRAND } from "../constants";
import { getTributeUrl, getShareText, openFacebookShare, openEmailShare } from "../utils/shareUtils.js";

var hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

export default function ShareMenu({ tribute }) {
  var [open, setOpen] = useState(false);
  var [copied, setCopied] = useState(false);
  var menuRef = useRef(null);
  var tributeUrl = getTributeUrl(tribute.id);
  var shareText = getShareText(tribute);

  useEffect(function() {
    if (!open) return;
    var handleClick = function(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return function() { document.removeEventListener("mousedown", handleClick); };
  }, [open]);

  var handleCopy = function() {
    navigator.clipboard.writeText(tributeUrl).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); setOpen(false); }, 1200);
    }).catch(function() {});
  };

  var handleNativeShare = function(e) {
    e.stopPropagation();
    navigator.share({ title: shareText, url: tributeUrl }).catch(function() {});
  };

  // On mobile with native share, use it as the primary action (no dropdown)
  if (hasNativeShare) {
    return (
      <button
        onClick={handleNativeShare}
        style={{
          background: "rgba(0,0,0,0.05)",
          border: "none",
          borderRadius: "4px",
          padding: "6px 14px",
          fontSize: "12px",
          color: "#888",
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}
        onMouseOver={function(e) { e.currentTarget.style.background = "rgba(0,0,0,0.08)"; }}
        onMouseOut={function(e) { e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        Share
      </button>
    );
  }

  // Desktop fallback: dropdown with Copy, Facebook, Email
  var shareOptions = [
    {
      label: copied ? "Copied!" : "Copy link",
      icon: copied
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={BRAND.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>,
      onClick: handleCopy,
      color: copied ? BRAND.primary : "#555",
    },
    {
      label: "Facebook",
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>,
      onClick: function() { openFacebookShare(tributeUrl); setOpen(false); },
      color: "#555",
    },
    {
      label: "Email",
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 4L12 13 2 4" />
            </svg>,
      onClick: function() { openEmailShare(tributeUrl, tribute); setOpen(false); },
      color: "#555",
    },
  ];

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        onClick={function(e) { e.stopPropagation(); setOpen(!open); }}
        style={{
          background: "rgba(0,0,0,0.05)",
          border: "none",
          borderRadius: "4px",
          padding: "6px 14px",
          fontSize: "12px",
          color: "#888",
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}
        onMouseOver={function(e) { e.currentTarget.style.background = "rgba(0,0,0,0.08)"; }}
        onMouseOut={function(e) { e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        Share
      </button>
      {open && (
        <div
          onClick={function(e) { e.stopPropagation(); }}
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            right: 0,
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.08)",
            padding: "6px",
            minWidth: "160px",
            zIndex: 10,
            animation: "fadeIn 0.15s ease",
          }}
        >
          {shareOptions.map(function(opt, i) {
            return (
              <button
                key={i}
                onClick={opt.onClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "100%",
                  padding: "9px 12px",
                  border: "none",
                  borderRadius: "5px",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: opt.color,
                  fontWeight: opt.label === "Copied!" ? 600 : 400,
                  transition: "background 0.1s",
                  textAlign: "left",
                }}
                onMouseOver={function(e) { e.currentTarget.style.background = "#F5F5F5"; }}
                onMouseOut={function(e) { e.currentTarget.style.background = "transparent"; }}
              >
                {opt.icon}
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
